import React, { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface MetaOAuthCallbackProps {
  onComplete: () => void;
}

interface AccountToConnect {
  platform: 'facebook' | 'instagram' | 'instagram_direct';
  page_id: string;
  ig_business_id?: string;
  instagram_account_id?: string;
  page_name: string;
  access_token: string;
  permissions: string[];
  webhooks_subscribed: boolean;
  token_expires_at?: string;
  conflict?: {
    existingAccountId: string;
    existingOwnerUserId: string;
  };
}

export function MetaOAuthCallback({ onComplete }: MetaOAuthCallbackProps) {
  const [status, setStatus] = useState<'loading' | 'selecting' | 'saving' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Łączenie konta Meta...');
  const [availableAccounts, setAvailableAccounts] = useState<AccountToConnect[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [showAlreadyConnected, setShowAlreadyConnected] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      const url = new URL(window.location.href);
      const code = url.searchParams.get('code');
      const error = url.searchParams.get('error');
      const state = url.searchParams.get('state');

      if (error) {
        setStatus('error');
        setMessage(`Błąd autoryzacji: ${error}`);
        toast.error('Autoryzacja nie powiodła się');
        setTimeout(onComplete, 3000);
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage('Brak kodu autoryzacji');
        toast.error('Brak kodu autoryzacji');
        setTimeout(onComplete, 3000);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setStatus('error');
        setMessage('Nie jesteś zalogowany');
        setTimeout(onComplete, 3000);
        return;
      }

      setCurrentUserId(user.id);

      // CRITICAL: Check if this is Instagram Direct callback by looking at state parameter
      // Instagram Direct OAuth URLs should include state=instagram_direct
      const isInstagramDirectFlag = state === 'instagram_direct';

      console.log('[Meta OAuth] Detected flow type:', {
        state,
        isInstagramDirect: isInstagramDirectFlag,
      });

      // CRITICAL: redirect_uri must match EXACTLY what was used in OAuth URL
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const defaultRedirectUri = `${supabaseUrl}/functions/v1/meta-oauth-callback`;

      let redirectUri = defaultRedirectUri;

      // For Instagram Direct, extract redirect_uri from saved OAuth URL
      if (isInstagramDirectFlag) {
        const { data: settings } = await supabase
          .from('user_settings')
          .select('instagram_direct_oauth_url')
          .eq('user_id', user.id)
          .maybeSingle();

        if (settings?.instagram_direct_oauth_url) {
          try {
            const oauthUrl = new URL(settings.instagram_direct_oauth_url);
            const extractedRedirectUri = oauthUrl.searchParams.get('redirect_uri');
            if (extractedRedirectUri) {
              redirectUri = extractedRedirectUri;
              console.log('[Instagram Direct] Using redirect_uri from OAuth URL:', redirectUri);
            } else {
              console.log('[Instagram Direct] No redirect_uri in OAuth URL, using default:', defaultRedirectUri);
            }
          } catch (e) {
            console.warn('[Instagram Direct] Failed to parse OAuth URL, using default:', e);
          }
        }
      } else {
        // For regular Meta OAuth (Facebook/Instagram Business)
        // Use the callback page URL since that's what Meta OAuth uses
        redirectUri = 'https://sms.aiagenthub.pl/auth/meta/callback';
        console.log('[Meta OAuth] Using standard callback redirect_uri:', redirectUri);
      }

      console.log('[Meta OAuth] Making request:', {
        function: 'meta-oauth-callback',
        isInstagramDirect: isInstagramDirectFlag,
        redirectUri,
        codeLength: code?.length
      });

      // Use Supabase client to call edge function
      const { data, error: functionError } = await supabase.functions.invoke('meta-oauth-callback', {
        body: {
          code,
          redirectUri,
          userId: user.id,
          isInstagramDirect: isInstagramDirectFlag,
        },
      });

      console.log('[Meta OAuth] Response:', { data, error: functionError });

      if (functionError) {
        console.error('[Meta OAuth] Function error:', functionError);
        throw new Error(functionError.message || 'Nie udało się połączyć konta');
      }

      if (data?.error) {
        console.error('[Meta OAuth] Error response:', data);
        const errorMsg = data.details ? `${data.error}: ${data.details}` : data.error;
        throw new Error(errorMsg);
      }

      if (!data.success || !data.accounts || data.accounts.length === 0) {
        setStatus('error');
        setMessage('Nie znaleziono kont do podłączenia');
        toast.error('Nie znaleziono kont Facebook/Instagram Business');
        setTimeout(onComplete, 3000);
        return;
      }

      const accountsWithConflicts: AccountToConnect[] = [];
      const preselected = new Set<string>();

      for (const account of data.accounts) {
        const accountKey = `${account.platform}-${account.platform === 'facebook' ? account.page_id : (account.instagram_account_id || account.ig_business_id)}`;

        const lookupField = account.platform === 'facebook' ? 'page_id' : 'instagram_account_id';
        const lookupValue = account.platform === 'facebook' ? account.page_id : (account.instagram_account_id || account.ig_business_id);

        const { data: existing } = await supabase
          .from('social_accounts')
          .select('id, owner_user_id')
          .eq('platform', account.platform)
          .eq(lookupField, lookupValue)
          .maybeSingle();

        const accountWithConflict = { ...account };
        if (existing) {
          accountWithConflict.conflict = {
            existingAccountId: existing.id,
            existingOwnerUserId: existing.owner_user_id,
          };
          if (existing.owner_user_id === user.id) {
            preselected.add(accountKey);
          }
        } else {
          preselected.add(accountKey);
        }
        accountsWithConflicts.push(accountWithConflict);
      }

      setAvailableAccounts(accountsWithConflicts);
      setSelectedAccounts(preselected);
      setStatus('selecting');
      setMessage('Wybierz konta do podłączenia');
    } catch (error) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Nieznany błąd');
      toast.error('Nie udało się połączyć konta');
      setTimeout(onComplete, 3000);
    }
  };

  const toggleAccount = (accountKey: string) => {
    const newSelected = new Set(selectedAccounts);
    if (newSelected.has(accountKey)) {
      newSelected.delete(accountKey);
    } else {
      newSelected.add(accountKey);
    }
    setSelectedAccounts(newSelected);
  };

  const saveSelectedAccounts = async () => {
    setStatus('saving');
    setMessage('Zapisywanie kont...');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Nie jesteś zalogowany');

      let savedCount = 0;
      const errors: string[] = [];

      console.log('[Meta OAuth] Starting save. Selected accounts:', Array.from(selectedAccounts));
      console.log('[Meta OAuth] Available accounts:', availableAccounts.map(a => ({
        platform: a.platform,
        page_id: a.page_id,
        ig_business_id: a.ig_business_id,
        instagram_account_id: a.instagram_account_id,
        page_name: a.page_name,
        hasConflict: !!a.conflict,
        conflictOwner: a.conflict?.existingOwnerUserId,
      })));

      for (const account of availableAccounts) {
        const accountKey = `${account.platform}-${account.platform === 'facebook' ? account.page_id : (account.instagram_account_id || account.ig_business_id)}`;

        console.log(`[Meta OAuth] Checking account: ${account.page_name} (key: ${accountKey}), selected: ${selectedAccounts.has(accountKey)}`);

        if (!selectedAccounts.has(accountKey)) continue;

        // Tokeny stron Facebooka (i kont Instagram Business podpietych do strony)
        // nie maja daty waznosci - Graph API nie zwraca dla nich `expires_in`.
        // Nie wolno tu wstawiac sztucznego "teraz + 60 dni", bo po 60 dniach
        // system sam oznaczal dzialajacy token jako wygasly. Ich waznosc
        // weryfikuje validate_meta_tokens() bezposrednio w Graph API.
        // Instagram Direct uzywa dlugozyciowego tokenu uzytkownika, ktory ma
        // prawdziwa 60-dniowa waznosc - tam fallback zostaje.
        const tokenExpiresAt = account.token_expires_at ||
          (account.platform === 'instagram_direct'
            ? new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString()
            : null);

        const accountData = {
          owner_user_id: user.id,
          platform: account.platform,
          page_id: account.page_id,
          instagram_account_id: account.instagram_account_id || account.ig_business_id || null,
          page_name: account.page_name,
          access_token: account.access_token,
          permissions: account.permissions || [],
          token_expires_at: tokenExpiresAt,
          status: 'active',
        };

        try {
          if (account.conflict) {
            if (account.conflict.existingOwnerUserId === user.id) {
              // Update existing account owned by current user
              console.log(`[Meta OAuth] Updating existing account: ${account.page_name} (${account.conflict.existingAccountId})`);
              const { error: updateError } = await supabase
                .from('social_accounts')
                .update({
                  access_token: account.access_token,
                  permissions: account.permissions || [],
                  token_expires_at: tokenExpiresAt,
                  status: 'active',
                  page_name: account.page_name,
                  updated_at: new Date().toISOString(),
                })
                .eq('id', account.conflict.existingAccountId);

              if (updateError) {
                console.error('[Meta OAuth] Error updating account:', updateError);
                errors.push(`${account.page_name}: ${updateError.message}`);
                continue;
              }

              // Sync to connected_accounts (scheduler) if matching record exists
              if (account.page_id) {
                await supabase
                  .from('connected_accounts')
                  .update({
                    access_token: account.access_token,
                    token_expires_at: tokenExpiresAt,
                    status: 'active',
                  })
                  .eq('id', account.page_id)
                  .eq('user_id', user.id);
              }
            } else {
              // Take over account from another user
              console.log(`[Meta OAuth] Taking over account: ${account.page_name} from user ${account.conflict.existingOwnerUserId}`);
              
              // First delete the old account
              const { error: deleteError } = await supabase
                .from('social_accounts')
                .delete()
                .eq('id', account.conflict.existingAccountId);

              if (deleteError) {
                console.error('[Meta OAuth] Error deleting old account:', deleteError);
                errors.push(`${account.page_name}: nie udało się przejąć konta - ${deleteError.message}`);
                continue;
              }

              // Then insert the new one
              const { error: insertError } = await supabase
                .from('social_accounts')
                .insert(accountData);

              if (insertError) {
                console.error('[Meta OAuth] Error inserting after takeover:', insertError);
                errors.push(`${account.page_name}: ${insertError.message}`);
                continue;
              }
            }
          } else {
            // Brand new account - no conflict
            console.log(`[Meta OAuth] Inserting new account: ${account.page_name}`);
            
            // Double-check for duplicate before inserting
            const lookupField = account.platform === 'facebook' ? 'page_id' : 'instagram_account_id';
            const lookupValue = account.platform === 'facebook' ? account.page_id : (account.instagram_account_id || account.ig_business_id);
            
            const { data: existingCheck } = await supabase
              .from('social_accounts')
              .select('id, owner_user_id')
              .eq('platform', account.platform)
              .eq(lookupField, lookupValue)
              .eq('owner_user_id', user.id)
              .maybeSingle();

            if (existingCheck) {
              // Already exists for this user - update instead
              console.log(`[Meta OAuth] Account already exists, updating instead: ${account.page_name}`);
              const { error: updateError } = await supabase
                .from('social_accounts')
                .update({
                  access_token: account.access_token,
                  permissions: account.permissions || [],
                  token_expires_at: tokenExpiresAt,
                  status: 'active',
                  page_name: account.page_name,
                  updated_at: new Date().toISOString(),
                })
                .eq('id', existingCheck.id);

              if (updateError) {
                console.error('[Meta OAuth] Error updating existing:', updateError);
                errors.push(`${account.page_name}: ${updateError.message}`);
                continue;
              }
            } else {
              // Insert new account
              const { error: insertError } = await supabase
                .from('social_accounts')
                .insert(accountData);

              if (insertError) {
                console.error('[Meta OAuth] Error inserting new account:', insertError);
                console.error('[Meta OAuth] Error code:', insertError.code, 'Details:', insertError.details, 'Hint:', insertError.hint);
                console.error('[Meta OAuth] Account data was:', JSON.stringify({
                  ...accountData,
                  access_token: accountData.access_token?.substring(0, 20) + '...',
                }, null, 2));
                
                // If unique constraint violation, try update instead
                if (insertError.code === '23505') {
                  console.log(`[Meta OAuth] Duplicate detected for: ${account.page_name}, updating instead`);
                  
                  const lookupF = account.platform === 'facebook' ? 'page_id' : 'instagram_account_id';
                  const lookupV = account.platform === 'facebook' ? account.page_id : (account.instagram_account_id || account.ig_business_id);
                  
                  const { error: updateErr } = await supabase
                    .from('social_accounts')
                    .update({
                      access_token: account.access_token,
                      permissions: account.permissions || [],
                      token_expires_at: tokenExpiresAt,
                      status: 'active',
                      page_name: account.page_name,
                      updated_at: new Date().toISOString(),
                    })
                    .eq('platform', account.platform)
                    .eq(lookupF, lookupV)
                    .eq('owner_user_id', user.id);

                  if (updateErr) {
                    console.error('[Meta OAuth] Update after duplicate also failed:', updateErr);
                    errors.push(`${account.page_name}: ${updateErr.message}`);
                    continue;
                  }
                  console.log(`[Meta OAuth] Update after duplicate succeeded for: ${account.page_name}`);
                } else {
                  errors.push(`${account.page_name}: ${insertError.message}`);
                  continue;
                }
              } else {
                // Verify insertion by checking if the record exists
                const verifyField = account.platform === 'facebook' ? 'page_id' : 'instagram_account_id';
                const verifyValue = account.platform === 'facebook' ? account.page_id : (account.instagram_account_id || account.ig_business_id);
                
                const { data: verifyData } = await supabase
                  .from('social_accounts')
                  .select('id')
                  .eq('platform', account.platform)
                  .eq(verifyField, verifyValue)
                  .eq('owner_user_id', user.id)
                  .maybeSingle();

                if (verifyData) {
                  console.log(`[Meta OAuth] Insert verified for: ${account.page_name}, id: ${verifyData.id}`);
                } else {
                  console.warn(`[Meta OAuth] Insert returned no error but record not found for: ${account.page_name}. Possible RLS issue.`);
                  errors.push(`${account.page_name}: Zapis nie powiódł się (konto nie zostało znalezione po zapisie)`);
                  continue;
                }
              }
            }
          }

          savedCount++;
          console.log(`[Meta OAuth] Successfully saved: ${account.page_name} (${savedCount} total)`);
        } catch (accountError) {
          console.error(`[Meta OAuth] Unexpected error saving ${account.page_name}:`, accountError);
          errors.push(`${account.page_name}: ${accountError instanceof Error ? accountError.message : 'Nieznany błąd'}`);
        }
      }

      console.log(`[Meta OAuth] Save complete. Saved: ${savedCount}, Errors: ${errors.length}`);

      if (savedCount === 0 && errors.length > 0) {
        setStatus('error');
        setMessage(`Błąd zapisu: ${errors.join(', ')}`);
        toast.error(`Nie udało się zapisać kont: ${errors[0]}`);
      } else if (savedCount === 0) {
        setStatus('error');
        setMessage('Nie wybrano żadnych kont');
        toast.error('Nie wybrano żadnych kont');
      } else if (errors.length > 0) {
        setStatus('success');
        setMessage(`Połączono ${savedCount} kont (${errors.length} błędów)`);
        toast.success(`Połączono ${savedCount} kont`);
      } else {
        setStatus('success');
        setMessage(`Połączono ${savedCount} kont!`);
        toast.success(`Pomyślnie połączono ${savedCount} kont`);
      }

      setTimeout(onComplete, 2000);
    } catch (error) {
      console.error('Save accounts error:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Nieznany błąd');
      toast.error('Nie udało się zapisać kont');
      setTimeout(onComplete, 3000);
    }
  };

  if (status === 'selecting') {
    // Filter accounts based on showAlreadyConnected state
    const displayedAccounts = showAlreadyConnected
      ? availableAccounts
      : availableAccounts.filter(account =>
          !account.conflict || account.conflict.existingOwnerUserId !== currentUserId
        );

    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="bg-gray-800 rounded-lg p-8 max-w-2xl w-full">
          <Users className="w-16 h-16 text-[#7B4BFF] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Wybierz konta</h2>
          <p className="text-gray-400 mb-4 text-center">Zaznacz konta, które chcesz podłączyć</p>

          <div className="flex items-center justify-center gap-2 mb-6 p-3 bg-gray-700/50 rounded-lg">
            <input
              type="checkbox"
              id="showAlreadyConnected"
              checked={showAlreadyConnected}
              onChange={(e) => setShowAlreadyConnected(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-[#7B4BFF] focus:ring-[#7B4BFF] focus:ring-offset-gray-800"
            />
            <label htmlFor="showAlreadyConnected" className="text-sm text-gray-300 cursor-pointer">
              Pokaż już podłączone konta ({availableAccounts.filter(a => a.conflict?.existingOwnerUserId === currentUserId).length})
            </label>
          </div>

          {displayedAccounts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400">Wszystkie konta są już podłączone</p>
              <p className="text-sm text-gray-500 mt-2">
                Zaznacz opcję powyżej, aby zaktualizować uprawnienia
              </p>
            </div>
          ) : (
            <div className="space-y-3 mb-6">
              {displayedAccounts.map((account) => {
                const accountKey = `${account.platform}-${account.platform === 'facebook' ? account.page_id : (account.instagram_account_id || account.ig_business_id)}`;
                const isSelected = selectedAccounts.has(accountKey);
                const hasConflict = !!account.conflict;
                const isOwnAccount = account.conflict?.existingOwnerUserId === currentUserId;

                return (
                  <div
                    key={accountKey}
                    onClick={() => toggleAccount(accountKey)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-[#7B4BFF] bg-[#7B4BFF]/10'
                        : 'border-gray-700 bg-gray-700/50 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-medium">{account.page_name}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            account.platform === 'facebook'
                              ? 'bg-blue-500/20 text-blue-400'
                              : account.platform === 'instagram_direct'
                              ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-pink-400'
                              : 'bg-pink-500/20 text-pink-400'
                          }`}>
                            {account.platform === 'instagram_direct' ? 'instagram direct' : account.platform}
                          </span>
                        </div>
                        {hasConflict && (
                          <div className="flex items-start gap-2 mt-2">
                            <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                              isOwnAccount ? 'text-blue-400' : 'text-yellow-500'
                            }`} />
                            <p className={`text-sm ${
                              isOwnAccount ? 'text-blue-400' : 'text-yellow-500'
                            }`}>
                              {isOwnAccount
                                ? 'To konto jest już podłączone. Zaznaczenie zaktualizuje token i uprawnienia.'
                                : 'To konto jest już podłączone przez innego użytkownika. Wybranie go przejmie kontrolę.'}
                            </p>
                          </div>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-[#7B4BFF] focus:ring-[#7B4BFF] focus:ring-offset-gray-800"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => {
                setStatus('error');
                setMessage('Anulowano');
                setTimeout(onComplete, 1000);
              }}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={saveSelectedAccounts}
              disabled={selectedAccounts.size === 0}
              className="flex-1 px-4 py-2 bg-[#7B4BFF] text-white rounded-lg hover:bg-[#6A3FEF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Połącz ({selectedAccounts.size})
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
      <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <RefreshCw className="w-16 h-16 text-[#7B4BFF] mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-white mb-2">Łączenie konta</h2>
            <p className="text-gray-400">{message}</p>
          </>
        )}

        {status === 'saving' && (
          <>
            <RefreshCw className="w-16 h-16 text-[#7B4BFF] mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-white mb-2">Zapisywanie</h2>
            <p className="text-gray-400">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Sukces!</h2>
            <p className="text-gray-400">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Błąd</h2>
            <p className="text-gray-400">{message}</p>
          </>
        )}
      </div>
    </div>
  );
}
